import { getTimeAtFrame } from '@helpers/avInfo';
import { useTypedSelector } from '@hooks';
import { useCallback, useEffect, useRef } from 'react';
import { VideoJsPlayer } from 'video.js';

type useKeysProps = {
  duration: number;
  isPaused: boolean;
  player: VideoJsPlayer;
  isLooped: boolean;
  onFastForward: (v: number) => void;
  setPlaybackSpeed: (v: number) => void;
};
export default function useKeys({
  onFastForward,
  setPlaybackSpeed,
  duration,
  isLooped,
  isPaused,
  player
}: useKeysProps) {
  const speedRef = useRef(0);
  const reverseTargetTimeRef = useRef<number | null>(null);
  const isReverseRunningRef = useRef(false);
  const selectedVersion = useTypedSelector(
    ({ mediaViewer }) => mediaViewer.selectedVersion
  );
  const isCommentFieldFocused = useTypedSelector(
    ({ mediaViewer }) => mediaViewer.isCommentFieldFocused
  );

  const intervalRewind = useRef<number>(0);
  const EDGE_EPSILON = 0.001;

  const onReverseSeek = (time: number) => {
    if (speedRef.current >= 0) return;
    reverseTargetTimeRef.current = time;
    player.currentTime(time);
  };

  const onExternalPlaybackRateChange = useCallback(
    (value: number) => {
      cancelAnimationFrame(intervalRewind.current);
      isReverseRunningRef.current = false;
      reverseTargetTimeRef.current = null;
      const wasReverse = speedRef.current < 0;
      speedRef.current = value;
      setPlaybackSpeed(value);
      player.playbackRate(value);
      if (wasReverse) {
        player.play();
      }
    },
    [player, setPlaybackSpeed]
  );

  const KeyL = useCallback(
    (multiplier: number) => {
      cancelAnimationFrame(intervalRewind.current);
      isReverseRunningRef.current = false;
      reverseTargetTimeRef.current = null;
      speedRef.current = multiplier;
      setPlaybackSpeed(multiplier);
      player.playbackRate(multiplier);
      player.play();
    },
    [player, setPlaybackSpeed]
  );

  const KeyJ = useCallback(
    (multiplier: number) => {
      speedRef.current = multiplier;
      isReverseRunningRef.current = true;
      setPlaybackSpeed(multiplier);

      player.pause();

      let lastTimestamp: number | null = null;
      const startTime = player.currentTime();
      const reverseStartTime =
        startTime <= EDGE_EPSILON && duration > 0 ? duration : startTime;
      reverseTargetTimeRef.current = reverseStartTime;
      player.currentTime(reverseStartTime);

      cancelAnimationFrame(intervalRewind.current);
      const play = (timestamp: number) => {
        if (!isReverseRunningRef.current) return;
        if (lastTimestamp === null) {
          lastTimestamp = timestamp;
          intervalRewind.current = requestAnimationFrame(play);
          return;
        }

        const elapsedSeconds = (timestamp - lastTimestamp) / 1000;
        lastTimestamp = timestamp;

        // Drive reverse playback from a virtual timeline to avoid seek jitter
        // distorting effective speed.
        const currentVirtualTime =
          reverseTargetTimeRef.current ?? player.currentTime();
        let virtualTime =
          currentVirtualTime - elapsedSeconds * Math.abs(multiplier);
        reverseTargetTimeRef.current = virtualTime;

        if (virtualTime <= 0) {
          if (isLooped) {
            virtualTime = duration;
            reverseTargetTimeRef.current = duration;
            player.currentTime(duration);
          } else {
            isReverseRunningRef.current = false;
            reverseTargetTimeRef.current = 0;
            player.currentTime(0);
            cancelAnimationFrame(intervalRewind.current);
            return;
          }
        }

        if (!player.seeking()) {
          player.currentTime(virtualTime);
        }

        intervalRewind.current = requestAnimationFrame(play);
      };
      intervalRewind.current = requestAnimationFrame(play);
    },
    [duration, isLooped, player, setPlaybackSpeed]
  );

  const playPause = useCallback(() => {
    if (isPaused) {
      if (speedRef.current < 0) {
        const baseTime = reverseTargetTimeRef.current ?? player.currentTime();
        const targetTime =
          baseTime <= EDGE_EPSILON && duration > 0 ? duration : baseTime;

        if (isReverseRunningRef.current) {
          cancelAnimationFrame(intervalRewind.current);
          isReverseRunningRef.current = false;
          player.currentTime(targetTime);
          return;
        }

        player.currentTime(targetTime);
        KeyJ(speedRef.current);
        return;
      }
      const currentRate = player.playbackRate() || 1;
      player.playbackRate(currentRate);
      setPlaybackSpeed(currentRate);
      player.play();
      speedRef.current = currentRate;
    } else {
      player.pause();
    }
  }, [duration, isPaused, player, setPlaybackSpeed, KeyJ]);

  useEffect(() => {
    if (isCommentFieldFocused) return;

    const onKeyPress = (e: any) => {
      const modalElement = document.querySelector('.ant-modal-root');
      const fieldElement = document.querySelector(
        '.media_viewer_filter-search-input'
      );
      const transcriptSearchInput = document.getElementById(
        'TRANSCRIPT_SEARCH_INPUT'
      );

      if (modalElement || fieldElement || transcriptSearchInput) return;

      if (e.code === 'ArrowLeft' || e.code === 'ArrowRight') {
        const dir = e.code === 'ArrowLeft' ? -1 : 1;

        // Stop any reverse rAF loop and capture its virtual time
        cancelAnimationFrame(intervalRewind.current);
        isReverseRunningRef.current = false;
        const baseTime = reverseTargetTimeRef.current ?? player.currentTime();
        reverseTargetTimeRef.current = null;
        speedRef.current = 0;
        setPlaybackSpeed(1);
        player.playbackRate(1);

        const metadata = selectedVersion?.metadata;
        const timePerFrame = getTimeAtFrame(1, metadata) ?? 1;
        const newTime = Math.max(0, baseTime + dir * timePerFrame);
        const clampedTime = Math.max(0, Math.min(duration, newTime));
        player.pause();
        player.currentTime(clampedTime);
      }

      if (e.code === 'KeyK') {
        cancelAnimationFrame(intervalRewind.current);
        isReverseRunningRef.current = false;
        reverseTargetTimeRef.current = null;
        speedRef.current = 0;
        setPlaybackSpeed(1);
        player.playbackRate(1);
        player.pause();
      }

      if (e.code === 'Space') {
        playPause();
      }

      if (e.code === 'KeyJ') {
        const speed = speedRef.current;
        if (speed > 1) {
          KeyL(speed - 1);
          return;
        }
        if (speed === 1) {
          KeyJ(-1);
          return;
        }
        const _speed = speed - 1 >= -8 ? speed - 1 || -1 : speed;
        KeyJ(_speed);
      }
      if (e.code === 'KeyL') {
        const speed = speedRef.current;
        if (speed < -1) {
          KeyJ(speed + 1);
          return;
        }
        if (speed === -1) {
          KeyL(1);
          return;
        }
        const _speed = speed + 1 > 8 ? speed : speed + 1 || 1;
        KeyL(_speed);
      }
    };

    document.addEventListener('keydown', onKeyPress);

    return () => {
      document.removeEventListener('keydown', onKeyPress);
    };
  }, [
    duration,
    isCommentFieldFocused,
    isLooped,
    isPaused,
    onFastForward,
    player,
    selectedVersion?.metadata,
    setPlaybackSpeed,
    playPause,
    KeyJ,
    KeyL
  ]);

  return {
    speedRef,
    isReverseRunningRef,
    playPause,
    onReverseSeek,
    onExternalPlaybackRateChange
  };
}
